<template>
  <div class="onboarding-container">
    <div class="container-fluid onboarding-shell py-5">
      <div class="row justify-content-center">
        <div class="col-12">
        
          <!-- Progress Steps -->
          <div class="card shadow-sm mb-4 border-0 rounded-4">
            <div class="card-body p-4 p-md-5">
              <div class="progress-stepper">
                <div class="step" :class="{ 'active': stepIndex === 1, 'completed': isStepCompleted(1) }" @click="goTo('general_info')">
                  <div class="step-icon-wrapper">
                    <div class="step-icon">
                      <AppIcon name="bi-check-lg" v-if="isStepCompleted(1)" class="bi bi-check-lg" />
                      <span v-else>1</span>
                    </div>
                  </div>
                  <div class="step-content">
                    <h6 class="step-title">{{$t('general_informations')}}</h6>
                    <p class="step-desc text-muted mb-0 small">{{$t('complete_business_info')}}</p>
                  </div>
                </div>

                <div class="step-divider" :class="{ 'active': stepIndex > 1 || isStepCompleted(1) }"></div>

                <div class="step" :class="{ 'active': stepIndex === 2, 'completed': isStepCompleted(2) }" @click="goTo('plans')">
                  <div class="step-icon-wrapper">
                    <div class="step-icon">
                      <AppIcon name="bi-check-lg" v-if="isStepCompleted(2)" class="bi bi-check-lg" />
                      <span v-else>2</span>
                    </div>
                  </div>
                  <div class="step-content">
                    <h6 class="step-title">Plans</h6>
                    <p class="step-desc text-muted mb-0 small">Choisir le bon plan</p>
                  </div>
                </div>

                <div class="step-divider" :class="{ 'active': stepIndex > 2 || isStepCompleted(2) }"></div>

                <div class="step" :class="{ 'active': stepIndex === 3, 'completed': isStepCompleted(3) }" @click="goTo('summary')">
                  <div class="step-icon-wrapper">
                    <div class="step-icon">
                      <AppIcon name="bi-check-lg" v-if="isStepCompleted(3)" class="bi bi-check-lg" />
                      <span v-else>3</span>
                    </div>
                  </div>
                  <div class="step-content">
                    <h6 class="step-title">Résumé</h6>
                    <p class="step-desc text-muted mb-0 small">Confirmer votre compte</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Content Area -->
          <div class="card shadow">
            <div class="card-body p-4">
              <div class="onboarding-trial-pill" role="note">
                <span class="onboarding-trial-pill-icon">
                  <AppIcon name="bi-gift" class="bi bi-gift" />
                </span>
                <span class="onboarding-trial-pill-copy">
                  <strong>30 jours gratuits</strong>
                  <small>Essai offert avec votre pack</small>
                </span>
                <AppIcon name="bi-stars" class="bi bi-stars onboarding-trial-pill-sparkle" />
              </div>

              <!-- General Info Step -->
              <div v-if="stepIndex === 1">
                <div class="d-flex justify-content-between align-items-center mb-4 onboarding-header mb-4">
                  <h5 class="mb-0">{{ $t('complete_business_info') }}</h5>
                  <button class="btn btn-success onboarding-next-btn" @click="goTo('plans')" :disabled="!isGeneralInfoValid">
                    {{$t('next')}} <AppIcon name="bi-arrow-right" class="bi bi-arrow-right ms-2" />
                  </button>
                </div>
                
                <!-- <div v-if="showAlert" class="alert alert-info d-flex justify-content-between align-items-center mb-4">
                  <div class="d-flex align-items-center">
                    <AppIcon name="bi-info-circle" class="bi bi-info-circle me-2" />
                    <span>{{$t('you_can_edit_this_later')}}</span>
                  </div>
                  <button type="button" class="btn-close" aria-label="Close" @click="showAlert = false"></button>
                </div> -->

                <div class="row g-4">
                  <div class="col-md-2">
                    <div class="mb-3 text-center">
                      <label class="form-label fw-bold d-block">{{$t('logo')}}</label>
                      <input type="file" style="display:none" ref="logoInput" @change="previewImage" accept="image/*">
                      <div @click="$refs.logoInput.click()" class="logo-upload-container mx-auto mb-2">
                        <img v-if="!loadingImage && business.logo" :src="business.logo" class="preview">
                        <div v-else-if="loadingImage" class="logo-loading">
                          <div class="spinner-border spinner-border-sm" role="status"></div>
                        </div>
                        <AppIcon name="bi-camera" v-else class="bi bi-camera" style="font-size: 2rem;" />
                      </div>
                      <small class="text-muted">{{$t('click_to_change')}}</small>
                    </div>
                  </div>

                  <div class="col-md-10">
                    <div class="row g-3">
                      <div class="col-12">
                        <div class="mb-3">
                          <label class="form-label fw-bold">{{$t('business_name')}} <span class="text-danger">*</span></label>
                          <input type="text" v-model="business.name" class="form-control" required>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="mb-3">
                          <label class="form-label fw-bold">{{$t('country')}}</label>
                          <select v-model="business.country" class="form-select" required @change="onCountryChange">
                            <option v-for="country in countries" :key="country.code" :value="country.value">
                              {{ $t(country.name) }}
                            </option>
                          </select>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="mb-3">
                          <label class="form-label fw-bold">{{$t('language')}}</label>
                          <select v-model="business.language" class="form-select" required @change="changeLanguage">
                            <option value="fr">🇫🇷 Français</option>
                            <option value="en">🇺🇸 English</option>
                            <option value="ar">🇲🇦 العربية</option>
                          </select>
                        </div>
                      </div>
                      <div class="col-12">
                        <div class="mb-3">
                          <label class="form-label fw-bold">{{$t('business_address')}}</label>
                          <textarea class="form-control" v-model="business.address" rows="2"></textarea>
                        </div>
                      </div>
                      
                      <!-- Extra Fields -->
                      <template v-if="business.extra_fields && business.extra_fields.length">
                        <div class="col-12">
                          <hr>
                          <h5 class="mb-3">{{$t('additional_information')}}</h5>
                        </div>
                        <div 
                          v-for="(field, index) in business.extra_fields" 
                          :key="index"
                          class="col-md-6"
                        >
                          <div class="mb-3">
                            <label class="form-label fw-bold">
                              {{ $t(field.field_name) }}
                              <span v-if="field.required" class="text-danger">*</span>
                            </label>
                            <input 
                              type="text" 
                              v-model="field.field_value" 
                              class="form-control"
                              :required="field.required"
                            >
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>

                <!-- Mobile bottom next button -->
                <div class="d-block d-md-none mt-4">
                  <button class="btn btn-success w-100 py-2" @click="goTo('plans')" :disabled="!isGeneralInfoValid">
                    {{$t('next')}} <AppIcon name="bi-arrow-right" class="bi bi-arrow-right ms-2" />
                  </button>
                </div>
              </div>

              <!-- Plans Step -->
              <div v-else-if="stepIndex === 2">
                <div class="d-flex justify-content-between align-items-center mb-4 onboarding-header">
                  <div>
                    <h5 class="mb-1">Choisissez votre plan</h5>
                    <p class="text-muted mb-0">Les modules inclus seront activés automatiquement.</p>
                  </div>
                  <button class="btn btn-success onboarding-next-btn" @click="goTo('summary')" :disabled="!isModuleSelected">
                    Continuer <AppIcon name="bi-arrow-right" class="bi bi-arrow-right ms-2" />
                  </button>
                </div>

                <div class="billing-switch mx-auto mb-4" role="group" aria-label="Choisir la période de facturation">
                  <button
                    type="button"
                    :class="{ active: billingCycle === 'monthly' }"
                    :aria-pressed="billingCycle === 'monthly'"
                    @click="setBillingCycle('monthly')"
                  >
                    Mensuel
                  </button>
                  <button
                    type="button"
                    :class="{ active: billingCycle === 'yearly' }"
                    :aria-pressed="billingCycle === 'yearly'"
                    @click="setBillingCycle('yearly')"
                  >
                    Annuel
                    <span>2 mois offerts</span>
                  </button>
                </div>

                <section class="plans-card-grid" aria-label="Choisir un plan GStocky">
                  <article
                    v-for="plan in pricingPlans"
                    :key="plan.key"
                    class="onboarding-package-card"
                    :class="{ recommended: plan.recommended, selected: isSelectedPricingPlan(plan) }"
                    @click="selectPricingPlan(plan.key)"
                  >
                    <span v-if="plan.recommended" class="plan-card-recommended-badge">Recommandé</span>

                    <header class="package-top">
                      <span class="plan-card-kicker">{{ planKicker(plan) }}</span>
                      <h3>{{ plan.name }}</h3>
                      <p>{{ plan.description }}</p>
                    </header>

                    <div class="package-price" :class="{ enterprise: plan.enterprise }">
                      <template v-if="plan.enterprise">
                        <strong class="package-price-quote">Sur devis</strong>
                        <small>Adapté à vos besoins</small>
                      </template>
                      <template v-else>
                        <span v-if="activeDiscountPercent" class="package-original-price">
                          {{ formatPlanAmount(plan[billingCycle]) }}
                        </span>
                        <div class="package-price-number">
                          <strong>{{ formatPlanAmount(discountedPlanPrice(plan)) }}</strong>
                          <span class="package-price-period">
                            {{ pricingCurrencySymbol }}/{{ billingCycle === 'monthly' ? 'Mois' : 'An' }}
                          </span>
                        </div>
                        <small>{{ billingCycle === 'monthly' ? 'Facturation mensuelle' : 'Facturation annuelle · 2 mois offerts' }}</small>
                        <small v-if="billingCycle === 'yearly'" class="package-saving">
                          Vous économisez {{ formatPlanAmount(yearlySavings(plan)) }} {{ pricingCurrencySymbol }} par an
                        </small>
                      </template>
                    </div>

                    <ul class="plan-stat-list">
                      <li><AppIcon name="bi-people" class="bi bi-people" /><span>{{ plan.users }}</span></li>
                      <li><AppIcon name="bi-hdd" class="bi bi-hdd" /><span>{{ planStorageLabel(plan) }}</span></li>
                    </ul>

                    <button
                      type="button"
                      class="plan-details-toggle"
                      :aria-expanded="expandedPlanKey === plan.key"
                      :aria-controls="`plan-details-${plan.key}`"
                      @click.stop="togglePlanDetails(plan.key)"
                    >
                      <span>{{ expandedPlanKey === plan.key ? 'Masquer les fonctionnalités' : 'Explorer les fonctionnalités' }}</span>
                      <AppIcon
                        :name="expandedPlanKey === plan.key ? 'bi-chevron-up' : 'bi-chevron-down'"
                        :class="expandedPlanKey === plan.key ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"
                      />
                    </button>

                    <div
                      v-if="expandedPlanKey === plan.key"
                      :id="`plan-details-${plan.key}`"
                      class="plan-expanded-details"
                      @click.stop
                    >
                      <section class="plan-modules-section">
                        <div class="package-section-heading">
                          <h4>Modules inclus</h4>
                          <span>{{ modulesForPlan(plan).length }}</span>
                        </div>
                        <div class="plan-module-grid">
                          <div
                            v-for="module in modulesForPlan(plan)"
                            :key="`${plan.key}-${module.module_key}`"
                            class="plan-module-item"
                          >
                            <span class="plan-module-icon" :style="moduleIconStyle(module.module_key)">
                              <AppIcon :name="module.icon || 'bi bi-grid'" :class="module.icon || 'bi bi-grid'" />
                            </span>
                            <span>{{ $t(module.title) || module.title }}</span>
                          </div>
                        </div>
                      </section>

                      <section class="plan-features-section">
                        <h4 class="package-section-title">Toutes les fonctionnalités</h4>
                        <ul class="package-feature-list">
                          <li v-for="feature in plan.features" :key="feature">
                            <AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill" />
                            <span>{{ feature }}</span>
                          </li>
                        </ul>
                      </section>
                    </div>

                    <button
                      type="button"
                      class="package-selection-state"
                      :class="{ selected: isSelectedPricingPlan(plan), primary: plan.recommended }"
                      :aria-pressed="isSelectedPricingPlan(plan)"
                      @click.stop="selectPricingPlanFromAction(plan.key)"
                    >
                      <AppIcon name="bi-check-circle-fill" v-if="isSelectedPricingPlan(plan)" class="bi bi-check-circle-fill" />
                      {{ isSelectedPricingPlan(plan) ? 'Plan sélectionné' : (plan.enterprise ? 'Choisir Enterprise' : `Choisir ${plan.name}`) }}
                    </button>
                  </article>
                </section>

                <div class="d-flex justify-content-end mt-4">
                  <button class="btn btn-outline-secondary me-2" @click="goTo('general_info')">
                    <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-2" /> {{$t('back')}}
                  </button>
                  <button class="btn btn-success" @click="goTo('summary')" :disabled="!isModuleSelected">
                    Continuer <AppIcon name="bi-arrow-right" class="bi bi-arrow-right ms-2" />
                  </button>
                </div>
              </div>

              <!-- Summary Step -->
              <div v-else-if="stepIndex === 3">
                <div class="d-flex justify-content-between align-items-center mb-4 onboarding-header">
                  <div>
                    <h5 class="mb-1">Résumé de votre compte</h5>
                    <p class="text-muted mb-0">Vérifiez les informations avant de créer votre espace.</p>
                  </div>
                  <button class="btn btn-success onboarding-next-btn" @click="save" :disabled="!canCreateAccount || loading">
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Create my account <AppIcon name="bi-check-lg" class="bi bi-check-lg ms-2" />
                  </button>
                </div>

                <div class="summary-layout">
                  <section class="summary-panel plan-summary-panel">
                    <div class="summary-panel-header">
                      <div>
                        <span>Pack</span>
                        <h6>{{ selectedPricingPlan.name }}</h6>
                      </div>
                    </div>

                    <div class="summary-plan-price">
                      <span v-if="activeDiscountPercent" class="summary-original-price">{{ selectedPlanOriginalPriceLabel }}</span>
                      <strong>{{ selectedPlanPriceLabel }}</strong>
                      <span>{{ selectedBillingLabel }}</span>
                    </div>

                    <div class="summary-badges">
                      <span v-if="activeDiscountPercent">Remise {{ activeDiscountPercent }}%</span>
                      <span v-if="activeOfferCode">Offre {{ activeOfferCode.toUpperCase() }}</span>
                      <span>{{ selectedPricingPlan.users }}</span>
                    </div>

                    <ul class="summary-feature-list">
                      <li v-for="feature in selectedPricingPlan.keyFeatures.slice(0, 5)" :key="feature">
                        <AppIcon name="bi-check2" class="bi bi-check2" />
                        <span>{{ feature }}</span>
                      </li>
                    </ul>
                  </section>

                  <section class="summary-panel">
                    <div class="summary-panel-header">
                      <div>
                        <span>Modules</span>
                        <h5 class="summary-modules-title">{{ enabledBusinessModules.length }} modules activés</h5>
                      </div>
                    </div>
                    <div class="summary-modules">
                      <div v-for="module in enabledBusinessModules" :key="module.module_key" class="summary-module-card">
                        <span class="summary-module-icon" :style="moduleIconStyle(module.module_key)">
                          <AppIcon :name="module.icon || 'bi bi-check-circle'" :class="module.icon || 'bi bi-check-circle'" />
                        </span>
                        <span class="summary-module-name">{{ $t(module.title) || module.title }}</span>
                      </div>
                    </div>
                  </section>
                </div>

                <div class="d-flex justify-content-between mt-4 summary-actions">
                  <button class="btn btn-outline-secondary" @click="goTo('plans')">
                    <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-2" /> Retour
                  </button>
                  <button class="btn btn-success" @click="save" :disabled="!canCreateAccount || loading">
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Create my account <AppIcon name="bi-check-lg" class="bi bi-check-lg ms-2" />
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>

        <!-- Logout Link -->
        <div class="text-center mt-4">
          <a href="#" @click.prevent="logOut" class="text-muted text-decoration-none">
            <AppIcon name="bi-box-arrow-right" class="bi bi-box-arrow-right me-1" /> {{ $t('logout') }}
          </a>
        </div>
        
      </div>
    </div>
  </div>
</template>

<script>
import helper from '../../helper'
import { PRICING_ADDONS, PRICING_MARKET_NOTES, PRICING_PLANS, applyDiscountToPrice, canPlanAccessModule, canPlanAccessModuleItem, convertPlanPrice, formatPlanAmount as formatLocalizedPlanAmount, getBrowserPricingCountryCode, getPlanCurrencySymbol, getPricingCurrency, getPricingPlan, getStoredPlanIntent, normalizeDiscountPercent, savePlanIntentFromQuery, clearStoredPlanIntent } from '@/constants/pricingPlans'

const DEFAULT_DISABLED_MODULE_ITEMS = {
  inventory: ['categories', 'warehouses'],
  sales: ['sales_insights'],
  purchases: ['purchase_insights'],
  finance: ['zakat']
}

export default {
  name: 'Onboarding',
  data() {
    return {
      stepIndex: 1,
      pricingPlans: PRICING_PLANS,
      marketNotes: PRICING_MARKET_NOTES,
      addons: PRICING_ADDONS,
      billingCycle: 'yearly',
      selectedPlanKey: null,
      selectedBillingCycleKey: null,
      expandedPlanKey: null,
      visitorCountryCode: null,
      showAlert: true,
      loading: false,
      loadingImage: false,
      logoFile: null,
      selectedProjectType: null,
    projectTypes: [
  { key: 'pos', title: 'pos_management', description: 'suitable_for_retail_shops_restaurants_supermarkets', icon: '/images/003-cashier.png' },
  { key: 'inventory', title: 'inventory_management', description: 'manage_products_stock_and_invoicing', icon: '/images/004-inventory-list.png' },
  { key: 'project', title: 'project_management', description: 'track_projects_tasks_and_progress', icon: '/images/001-manager.png' },
  { key: 'reservations', title: 'reservations', description: 'manage_bookings_and_reservations', icon: '/images/002-booked.png' },
  { key: 'subscriptions', title: 'subscriptions', description: 'membership_and_subscription_management', icon: '/images/005-gym.png' },
  { key: 'cash', title: 'cash_management', description: 'cash_description', icon: '/images/006-salary.png' },
  { key: 'files', title: 'file_management', description: 'manage_documents_and_attachments', icon: '/images/007-folder.png' },
  { key: 'hr', title: 'human_resources', description: 'manage_employees_leaves_and_payroll', icon: '/images/001-manager.png' },
  { key: 'contracts', title: 'contracts_management', description: 'manage_contracts_and_agreements', icon: '/images/007-folder.png' },
  { key: 'planning', title: 'planning_management', description: 'manage_schedules_and_planning', icon: '/images/002-booked.png' },
  { key: 'leads', title: 'leads_management', description: 'manage_leads_and_opportunities', icon: '/images/001-manager.png' },
  { key: 'custom', title: 'custom_modules', description: 'select_your_own_modules', icon: '/images/008-customization.png' }
    ],
      countries: [
        { code: 'MA', value: 'morocco', name: 'Morocco' },
        { code: 'FR', value: 'france', name: 'France' },
        { code: 'US', value: 'united_states', name: 'United States of America' },
        { code: 'DE', value: 'germany', name: 'Germany' },
        { code: 'EG', value: 'egypt', name: 'Egypt' },
        { code: 'ES', value: 'spain', name: 'Spain' },
        { code: 'GB', value: 'united_kingdom', name: 'United Kingdom' },
        { code: 'IT', value: 'italy', name: 'Italy' },
        { code: 'NL', value: 'netherlands', name: 'Netherlands' },
        { code: 'DZ', value: 'algeria', name: 'Algeria' },
        { code: 'TN', value: 'tunisia', name: 'Tunisia' },
        { code: 'AO', value: 'angola', name: 'Angola' },
        { code: 'BJ', value: 'benin', name: 'Benin' },
        { code: 'BW', value: 'botswana', name: 'Botswana' },
        { code: 'CD', value: 'democratic_republic_of_congo', name: 'Democratic Republic of the Congo' },
        { code: 'CF', value: 'central_african_republic', name: 'Central African Republic' },
        { code: 'CG', value: 'republic_of_congo', name: 'Republic of the Congo' },
        { code: 'CI', value: 'cote_d_ivoire', name: "Côte d'Ivoire" },
        { code: 'DJ', value: 'djibouti', name: 'Djibouti' },
        { code: 'ER', value: 'eritrea', name: 'Eritrea' },
        { code: 'ET', value: 'ethiopia', name: 'Ethiopia' },
        { code: 'GA', value: 'gabon', name: 'Gabon' },
        { code: 'GH', value: 'ghana', name: 'Ghana' },
        { code: 'GM', value: 'gambia', name: 'The Gambia' },
        { code: 'GN', value: 'guinea', name: 'Guinea' },
        { code: 'GQ', value: 'equatorial_guinea', name: 'Equatorial Guinea' },
        { code: 'KE', value: 'kenya', name: 'Kenya' },
        { code: 'KM', value: 'comoros', name: 'Comoros' },
        { code: 'LR', value: 'liberia', name: 'Liberia' },
        { code: 'LS', value: 'lesotho', name: 'Lesotho' },
        { code: 'LY', value: 'libya', name: 'Libya' },
        { code: 'MG', value: 'madagascar', name: 'Madagascar' },
        { code: 'ML', value: 'mali', name: 'Mali' },
        { code: 'MR', value: 'mauritania', name: 'Mauritania' },
        { code: 'MW', value: 'malawi', name: 'Malawi' },
        { code: 'MU', value: 'mauritius', name: 'Mauritius' },
        { code: 'MZ', value: 'mozambique', name: 'Mozambique' },
        { code: 'NA', value: 'namibia', name: 'Namibia' },
        { code: 'NE', value: 'niger', name: 'Niger' },
        { code: 'NG', value: 'nigeria', name: 'Nigeria' },
        { code: 'RE', value: 'réunion', name: 'Réunion' },
        { code: 'RW', value: 'rwanda', name: 'Rwanda' },
        { code: 'SC', value: 'seychelles', name: 'Seychelles' },
        { code: 'SD', value: 'sudan', name: 'Sudan' },
        { code: 'SH', value: 'saint_helena', name: 'Saint Helena, Ascension and Tristan da Cunha' },
        { code: 'SL', value: 'sierra_leone', name: 'Sierra Leone' },
        { code: 'SN', value: 'senegal', name: 'Senegal' },
        { code: 'SO', value: 'somalia', name: 'Somalia' },
        { code: 'SS', value: 'south_sudan', name: 'South Sudan' },
        { code: 'ST', value: 'são_tomé_and_príncipe', name: 'São Tomé and Principe' },
        { code: 'SZ', value: 'swaziland', name: 'Swaziland' },
        { code: 'TD', value: 'chad', name: 'Chad' },
        { code: 'TG', value: 'togo', name: 'Togo' },
        { code: 'TZ', value: 'tanzania', name: 'Tanzania' },
        { code: 'UG', value: 'uganda', name: 'Uganda' },
        { code: 'ZA', value: 'south_africa', name: 'South Africa' },
        { code: 'ZM', value: 'zambia', name: 'Zambia' },
        { code: 'ZW', value: 'zimbabwe', name: 'Zimbabwe' }
      ],
      business: {
        id: null,
        name: '',
        email: '',
        website: '',
        phone: '',
        address: '',
        logo: null,
        country: 'morocco',
        language: 'fr',
        currency: 'dh',
        vat: 20,
        billing_info: '',
        plan_id: null,
        trial_end_at: null,
        status: '',
        metadata: {},
        extra_fields: []
      },
      currencyMap: {
        'morocco': 'dh',
        'france': '€',
        'united_states': '$',
        'germany': '€',
        'egypt': 'EGP',
        'spain': '€',
        'united_kingdom': '£',
        'italy': '€',
        'netherlands': '€',
        'algeria': 'DZD',
        'tunisia': 'DT',
        'angola': 'AOA',
        'benin': 'XOF',
        'botswana': 'BWP',
        'democratic_republic_of_congo': 'CDF',
        'central_african_republic': 'XAF',
        'republic_of_congo': 'XAF',
        'cote_d_ivoire': 'XOF',
        'djibouti': 'DJF',
        'eritrea': 'ERN',
        'ethiopia': 'ETB',
        'gabon': 'XAF',
        'ghana': 'GHS',
        'gambia': 'GMD',
        'guinea': 'GNF',
        'equatorial_guinea': 'XAF',
        'kenya': 'KES',
        'comoros': 'KMF',
        'liberia': 'LRD',
        'lesotho': 'LSL',
        'libya': 'LYD',
        'madagascar': 'MGA',
        'mali': 'XOF',
        'mauritania': 'MRU',
        'malawi': 'MWK',
        'mauritius': 'MUR',
        'mozambique': 'MZN',
        'namibia': 'NAD',
        'niger': 'XOF',
        'nigeria': 'NGN',
        'réunion': '€',
        'rwanda': 'RWF',
        'seychelles': 'SCR',
        'sudan': 'SDG',
        'saint_helena': 'SHP',
        'sierra_leone': 'SLL',
        'senegal': 'XOF',
        'somalia': 'SOS',
        'south_sudan': 'SSP',
        'são_tomé_and_príncipe': 'STN',
        'swaziland': 'SZL',
        'chad': 'XAF',
        'togo': 'XOF',
        'tanzania': 'TZS',
        'uganda': 'UGX',
        'south_africa': 'R',
        'zambia': 'ZMW',
        'zimbabwe': 'ZWL'
      },
    }
  },
  computed: {
    modules() { return this.$store.state.modules },
    user() { return this.$store.getters.USER },
    isGeneralInfoValid() {
      return Boolean(
        this.business.name &&
        this.business.country &&
        this.business.language
      )
    },
    isModuleSelected() {
      return Boolean(this.selectedPlanKey)
    },
    canCreateAccount() {
      return this.isGeneralInfoValid && this.isModuleSelected;
    },
    selectedPricingPlan() {
      return getPricingPlan(this.selectedPlanKey)
    },
    activeDiscountPercent() {
      const storedIntent = getStoredPlanIntent();
      return normalizeDiscountPercent(
        storedIntent?.discount ??
        this.business?.metadata?.discount_percent ??
        this.business?.metadata?.subscription_discount_percent
      );
    },
    activeOfferCode() {
      const storedIntent = getStoredPlanIntent();
      return storedIntent?.offer || storedIntent?.offer_code || this.business?.metadata?.offer_code || null;
    },
    availableModulesForSelectedPlan() {
      return this.modules.filter(module => (
        module.module_key !== 'settings' &&
        this.isModuleAvailableForSelectedPlan(module)
      ))
    },
    enabledBusinessModules() {
      return this.modules.filter(module => (
        module.module_key !== 'settings' &&
        module.enabled &&
        this.isModuleAvailableForSelectedPlan(module)
      ));
    },
    selectedCountryLabel() {
      const country = this.countries.find(country => country.value === this.business.country);
      return country ? this.$t(country.name) : (this.business.country || '-');
    },
    selectedLanguageLabel() {
      const labels = {
        fr: 'Français',
        en: 'English',
        ar: 'العربية'
      };
      return labels[this.business.language] || this.business.language || '-';
    },
    selectedBillingLabel() {
      return this.billingCycle === 'monthly' ? 'Mensuel' : 'Annuel';
    },
    selectedPlanOriginalPriceLabel() {
      if (!this.selectedPricingPlan || this.selectedPricingPlan.enterprise) return 'Sur devis';
      return `${this.pricingCurrency === 'USD' ? '$' : ''}${this.formatPlanAmount(this.selectedPricingPlan[this.billingCycle])}${this.pricingCurrency === 'MAD' ? ' DH' : ''} HT`;
    },
    selectedPlanPriceLabel() {
      if (!this.selectedPricingPlan || this.selectedPricingPlan.enterprise) return 'Sur devis';
      return `${this.pricingCurrency === 'USD' ? '$' : ''}${this.formatPlanAmount(this.discountedPlanPrice(this.selectedPricingPlan))}${this.pricingCurrency === 'MAD' ? ' DH' : ''} HT`;
    },
    pricingCurrency() {
      return getPricingCurrency(this.visitorCountryCode);
    },
    pricingCurrencySymbol() {
      return getPlanCurrencySymbol(this.pricingCurrency);
    }
  },
  async created() {
    await this.detectUserLocation();
    await this.getBusiness();
    this.applySignupPlanIntent();
    await this.getModules();
    this.loadCountries();
  },
  mounted(){
    window.mixpanel.track("onboarding");
  },
  methods: {
    logOut() {
      localStorage.removeItem('access_token');
      this.$router.push({ path: '/login' });
    },
    applySignupPlanIntent() {
      const intent = savePlanIntentFromQuery(this.$route.query) || getStoredPlanIntent();
      if (!intent) return;

      const requestedBillingCycle = this.$route.query.interval || this.$route.query.billing || this.$route.query.billing_cycle;
      this.billingCycle = ['monthly', 'yearly'].includes(requestedBillingCycle)
        ? requestedBillingCycle
        : (intent.interval || 'yearly');
      this.business.metadata = {
        ...(this.business.metadata || {}),
        offer_code: intent.offer || intent.offer_code,
        discount_percent: intent.discount,
        subscription_discount_percent: intent.discount
      };

      // A signup intent can carry an offer or billing cycle, but onboarding must
      // always require the user to explicitly choose a plan.
      this.selectedPlanKey = null;
      this.selectedBillingCycleKey = null;
    },
    async detectUserLocation() {
      let edgeCountryCode = '';
      try {
        const response = await helper.customGet(
          `${process.env.VUE_APP_API}/location/country`,
          { silent: true, timeout: 4000 }
        );
        edgeCountryCode = String(response.data?.country_code || '').toUpperCase();
      } catch (error) {
        // Browser locale and timezone are used below when edge headers are unavailable.
      }

      try {
        // Get browser locale
        const locale = navigator.language || navigator.userLanguage || 'en-US';
        const countryCode = locale.split('-')[1]?.toUpperCase();
        
        // Get timezone
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        
        // Map country code to our country values
        const countryMapping = {
          'MA': 'morocco',
          'FR': 'france',
          'US': 'united_states',
          'DE': 'germany',
          'EG': 'egypt',
          'ES': 'spain',
          'GB': 'united_kingdom',
          'IT': 'italy',
          'NL': 'netherlands',
          'DZ': 'algeria',
          'TN': 'tunisia',
          'AO': 'angola',
          'BJ': 'benin',
          'BW': 'botswana',
          'CD': 'democratic_republic_of_congo',
          'CF': 'central_african_republic',
          'CG': 'republic_of_congo',
          'CI': 'cote_d_ivoire',
          'DJ': 'djibouti',
          'ER': 'eritrea',
          'ET': 'ethiopia',
          'GA': 'gabon',
          'GH': 'ghana',
          'GM': 'gambia',
          'GN': 'guinea',
          'GQ': 'equatorial_guinea',
          'KE': 'kenya',
          'KM': 'comoros',
          'LR': 'liberia',
          'LS': 'lesotho',
          'LY': 'libya',
          'MG': 'madagascar',
          'ML': 'mali',
          'MR': 'mauritania',
          'MW': 'malawi',
          'MU': 'mauritius',
          'MZ': 'mozambique',
          'NA': 'namibia',
          'NE': 'niger',
          'NG': 'nigeria',
          'RE': 'réunion',
          'RW': 'rwanda',
          'SC': 'seychelles',
          'SD': 'sudan',
          'SH': 'saint_helena',
          'SL': 'sierra_leone',
          'SN': 'senegal',
          'SO': 'somalia',
          'SS': 'south_sudan',
          'ST': 'são_tomé_and_príncipe',
          'SZ': 'swaziland',
          'TD': 'chad',
          'TG': 'togo',
          'TZ': 'tanzania',
          'UG': 'uganda',
          'ZA': 'south_africa',
          'ZM': 'zambia',
          'ZW': 'zimbabwe'
        };
        
        // Prioritize timezone detection for more accuracy
        let detectedCountry = countryMapping[edgeCountryCode] || null;
        if (/^[A-Z]{2}$/.test(edgeCountryCode)) {
          this.visitorCountryCode = edgeCountryCode;
        }
        if (!this.visitorCountryCode) {
          this.visitorCountryCode = getBrowserPricingCountryCode();
        }
        
        if (!detectedCountry && timezone) {
          // Detect from timezone first (more reliable)
          if (timezone.includes('Africa/Casablanca')) {
            detectedCountry = 'morocco';
          } else if (timezone.includes('Africa/Algiers')) {
            detectedCountry = 'algeria';
          } else if (timezone.includes('Europe/Paris')) {
            detectedCountry = 'france';
          } else if (timezone.includes('America/New_York') || timezone.includes('America/Los_Angeles') || timezone.includes('America/Chicago')) {
            detectedCountry = 'united_states';
          } else if (timezone.includes('Europe/London')) {
            detectedCountry = 'united_kingdom';
          } else if (timezone.includes('Europe/Berlin')) {
            detectedCountry = 'germany';
          } else if (timezone.includes('Africa/Cairo')) {
            detectedCountry = 'egypt';
          } else if (timezone.includes('Europe/Madrid')) {
            detectedCountry = 'spain';
          } else if (timezone.includes('Africa/Lagos')) {
            detectedCountry = 'nigeria';
          } else if (timezone.includes('Africa/Johannesburg')) {
            detectedCountry = 'south_africa';
          } else if (timezone.includes('Africa/Nairobi')) {
            detectedCountry = 'kenya';
          }
        }
        
        // Fallback to locale if timezone didn't match
        if (!detectedCountry && countryCode && countryMapping[countryCode]) {
          detectedCountry = countryMapping[countryCode];
          this.visitorCountryCode = countryCode;
        }

        if (!this.visitorCountryCode && detectedCountry) {
          this.visitorCountryCode = detectedCountry === 'morocco' ? 'MA' : 'XX';
        }
        
        // Set the detected country
        if (detectedCountry) {
          this.business.country = detectedCountry;
        }
        
        // Set currency based on detected country
        this.setCurrencyFromCountry();
        
        // Set language based on locale
        const languageCode = locale.split('-')[0]?.toLowerCase();
        if (languageCode === 'fr') {
          this.business.language = 'fr';
        } else if (languageCode === 'ar') {
          this.business.language = 'ar';
        } else {
          this.business.language = 'en';
        }

        this.changeLanguage();

        this.onCountryChange()
      } catch (error) {
        // Keep the Morocco defaults if location cannot be detected reliably.
      }
    },
    
    changeLanguage() {
      this.$i18n.locale = this.business.language;
      localStorage.setItem('currentLang', this.business.language);
    },
    
    setCurrencyFromCountry() {
      // Set currency based on country
      if (this.currencyMap[this.business.country]) {
        this.business.currency = this.currencyMap[this.business.country];
      }
    },
    
    onCountryChange() {
      // Reset extra fields when country changes
      // if (this.business.country === 'morocco') {
      //   this.business.extra_fields = [
      //     { field_name: 'ice', field_value: '', required: true },
      //     { field_name: 'if', field_value: '', required: true },
      //     { field_name: 'pt', field_value: '', required: true },
      //     { field_name: 'rc', field_value: '', required: true },
      //     { field_name: 'cnss', field_value: '', required: true },
      //     { field_name: 'capitale', field_value: '', required: true }
      //   ];
      // } else {
      //   this.business.extra_fields = [];
      // }

      this.business.extra_fields = [];

      
      // Update currency when country changes
      this.setCurrencyFromCountry();
    },
    
    toggleModule(module) {
      if (module.module_key === 'settings') return;
      if (!this.isModuleAvailableForSelectedPlan(module)) {
        module.enabled = false;
        return;
      }
      
      if (module.items) {
          module.items.forEach(item => {
            item.enabled = module.enabled;
          });
      }
    },
    getModuleColor(moduleKey) {
        const colors = {
            'dashboard': '#4a6cf7',
            'crm': '#00a09d',
            'inventory': '#d9363e',
            'sales': '#7c7bad',
            'purchases': '#875a7b',
            'pos': '#f06292',
            'finance': '#3eb4e4',
            'attachments': '#607d8b',
            'subscription_management': '#ff9800',
            'reservations': '#4caf50',
            'accountant': '#1976d2',
            'project_management': '#9c27b0',
            'hr': '#c44033',
            'advanced_insights': '#3f51b5',
            'contracts': '#795548',
            'planning': '#008784',
            'leads': '#e91e63',
            'ai': '#7c3aed',
            'marketing': '#ea580c',
            'importer': '#0891b2',
            'settings': '#4A4A4A'
        };
        return colors[moduleKey] || '#4A4A4A';
    },

    moduleIconStyle(moduleKey) {
      const color = this.getModuleColor(moduleKey);
      return {
        color,
        backgroundColor: `${color}14`
      };
    },

    modulesForPlan(plan) {
      if (!plan || !this.modules) return [];
      const includesAllModules = plan.moduleKeys.includes('*');

      return this.modules
        .filter(module => (
          module.module_key !== 'settings' &&
          (includesAllModules || plan.moduleKeys.includes(module.module_key))
        ))
        .slice()
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    },

    planKicker(plan) {
      const labels = {
        starter: 'Pour se lancer',
        business: 'Pour se structurer',
        pro: 'Pour accélérer',
        enterprise: 'Pour personnaliser'
      };
      return labels[plan.key] || plan.audience;
    },

    planStorageLabel(plan) {
      return plan.storageLimitGb ? `${plan.storageLimitGb} Go de stockage` : 'Stockage personnalisé';
    },

    togglePlanDetails(planKey) {
      this.expandedPlanKey = this.expandedPlanKey === planKey ? null : planKey;
    },

    yearlySavings(plan) {
      if (!plan || plan.enterprise) return 0;
      const yearlyMonthlyPrice = applyDiscountToPrice(plan.monthly * 12, this.activeDiscountPercent);
      return Math.max(0, yearlyMonthlyPrice - applyDiscountToPrice(plan.yearly, this.activeDiscountPercent));
    },

    formatPlanAmount(price) {
      return formatLocalizedPlanAmount(price, this.pricingCurrency);
    },

    discountedPlanPrice(plan) {
      if (!plan || plan.enterprise) return 0;
      return applyDiscountToPrice(plan[this.billingCycle], this.activeDiscountPercent);
    },

    setBillingCycle(cycle) {
      if (!['monthly', 'yearly'].includes(cycle)) return;
      this.billingCycle = cycle;
      this.selectedBillingCycleKey = cycle;
      this.business.metadata = {
        ...(this.business.metadata || {}),
        billing_cycle: cycle
      };
    },

    selectPricingPlan(planKey) {
      this.selectedPlanKey = planKey;
      this.selectedBillingCycleKey = this.billingCycle;
      this.business.plan_id = planKey;
      this.business.metadata = {
        ...(this.business.metadata || {}),
        plan_key: planKey,
        pricing_plan: planKey,
        plan_name: this.selectedPricingPlan.name,
        billing_cycle: this.billingCycle,
        offer_code: this.activeOfferCode,
        discount_percent: this.activeDiscountPercent,
        subscription_discount_percent: this.activeDiscountPercent,
        subscription_currency: this.pricingCurrency,
        original_plan_price: convertPlanPrice(this.selectedPricingPlan[this.billingCycle], this.pricingCurrency),
        discounted_plan_price: convertPlanPrice(this.discountedPlanPrice(this.selectedPricingPlan), this.pricingCurrency)
      };
      this.syncModulesWithSelectedPlan();
    },

    selectPricingPlanFromAction(planKey) {
      this.selectPricingPlan(planKey);
      if (window.matchMedia('(max-width: 767.98px)').matches) {
        this.goTo('summary');
      }
    },

    isSelectedPricingPlan(plan) {
      return this.selectedPlanKey === plan.key && this.selectedBillingCycleKey === this.billingCycle;
    },

    isModuleAvailableForSelectedPlan(module) {
      if (!module || module.module_key === 'settings') return true;
      return canPlanAccessModule(this.selectedPlanKey, module.module_key);
    },

    syncModulesWithSelectedPlan() {
      if (!this.selectedPlanKey || !this.modules || !this.modules.length) return;

      const syncedModules = this.modules.map(module => {
        const isSettings = module.module_key === 'settings';
        const isAvailable = isSettings || canPlanAccessModule(this.selectedPlanKey, module.module_key);

        return {
          ...module,
          enabled: isSettings ? true : isAvailable,
          expanded: ['business', 'pro'].includes(this.selectedPlanKey) ? false : (this.selectedPlanKey === 'starter' ? isAvailable : module.expanded),
          items: module.items
            ? module.items.map(item => {
                const isOptInReceipt = module.module_key === 'sales' && item.title === 'processes';
                const isDefaultDisabled = (DEFAULT_DISABLED_MODULE_ITEMS[module.module_key] || []).includes(item.title);
                return {
                  ...item,
                  enabled: isAvailable &&
                    (isSettings || canPlanAccessModuleItem(this.selectedPlanKey, module.module_key, item.permission, item.title)) &&
                    !isDefaultDisabled &&
                    (!isOptInReceipt || item.enabled === true)
                };
              })
            : []
        };
      });

      try {
        this.$store.commit('SET_MODULES', syncedModules);
      } catch (e) {
        // Store sync is best-effort; the local modules array remains the source for save().
      }
    },

    applySelectedPlanToModules() {
      this.syncModulesWithSelectedPlan();
    },
    
    isModuleEnabled(module) {
      if (!module.items) return false;
      return module.items.some(item => item.enabled);
    },
    
    async getBusiness() {
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/businesses`);
        if (response?.data?.user) {
          this.$store.commit('SET_USER', response.data.user);
        }
        this.$store.commit('SET_PERMISSIONS', {
          role: response?.data?.role,
          permissions: response?.data?.permissions
        });
        
        if (response?.data?.business) {
          // Handle business data
          const businessData = response.data.business;
          
          // Map business data to component state
          this.business = {
            id: businessData.id,
            name: businessData.name || '',
            email: businessData.email || '',
            phone: businessData.phone || '',
            website: businessData.website || '',
            address: businessData.address || '',
            vat: businessData.vat || 20,
            logo: businessData.logo || null,
            trial_end_at: businessData.trial_end_at,
            status: businessData.status,
            metadata: businessData.metadata || {},
            plan_id: businessData.plan_id || null,
            currency: businessData.currency || 'dh',
            language: businessData.language || 'fr',
            country: businessData.country || 'morocco',
            billing_info: businessData.billing_info || '',
            extra_fields: businessData.extra_fields || []
          };
          // Set language if available
          if (response.data.user?.language) {
            this.business.language = response.data.user.language;
            localStorage.setItem('currentLang', this.business.language);
            this.$i18n.locale = this.business.language;
          }
          
          // Load countries if needed
          if (!this.countries.length) {
            this.loadCountries();
          }
        }
      } catch (error) {
        this.$toast.error(this.$t('error_loading_business_data'));
      }
    },
    
    async getModules() {
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/modules`);
        if (response?.data?.length) {
          // Process each module and sort its items by position
          const processedModules = response.data.map(module => ({
            ...module,
            enabled: module.enabled || false,
            items: module.items 
              ? module.items
                  .filter(item => item.visible !== false)
                  .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                  .map(item => ({
                    ...item,
                    enabled: item.enabled || false
                  }))
              : []
          }));
          
          this.$store.commit('SET_MODULES', processedModules);
          
          this.syncModulesWithSelectedPlan();
          
          return processedModules;
        }
        return [];
      } catch (error) {
        this.$toast.error(this.$t('error_loading_modules'));
        return [];
      }
    },
    
    async save() {
      this.syncModulesWithSelectedPlan();

      // Check if at least one module is enabled
      const hasEnabledModule = this.modules.some(module => 
        module.module_key !== 'settings' && module.enabled
      );
      
      if (!hasEnabledModule) {
        this.$toast.error(this.$t('please_select_at_least_one_module'));
        return;
      }
      
      try {
        this.loading = true;
        
        // Prepare modules data for saving
        const modulesData = this.modules.map(module => ({
          module_key: module.module_key,
          enabled: module.enabled,
          items: module.items 
            ? module.items.map(item => ({
                item_key: item.item_key,
                enabled: item.enabled
              }))
            : []
        }));
        
        // Prepare business data with extra_fields
        const businessData = {
          ...this.business,
          metadata: {
            ...this.business.metadata,
            plan_key: this.selectedPlanKey,
            pricing_plan: this.selectedPlanKey,
            plan_name: this.selectedPricingPlan.name,
            billing_cycle: this.billingCycle,
            offer_code: this.activeOfferCode,
            discount_percent: this.activeDiscountPercent,
            subscription_discount_percent: this.activeDiscountPercent,
            subscription_currency: this.pricingCurrency,
            original_plan_price: convertPlanPrice(this.selectedPricingPlan[this.billingCycle], this.pricingCurrency),
            discounted_plan_price: convertPlanPrice(this.discountedPlanPrice(this.selectedPricingPlan), this.pricingCurrency),
          },
          plan_id: this.selectedPlanKey,
          modules: modulesData,
          extra_fields: this.business.extra_fields || []
        };

        this.business = {
          ...this.business,
          plan_id: this.selectedPlanKey,
          metadata: businessData.metadata
        };
        
        // Remove any empty strings or null values from extra_fields
        if (businessData.extra_fields) {
          businessData.extra_fields = businessData.extra_fields
            .filter(field => field.field_name && field.field_value);
        }
        

        if(localStorage.getItem('affiliate_ref')){
          businessData.affiliate_id = localStorage.getItem('affiliate_ref')
        }

        if (this.logoFile) {
          delete businessData.logo
        }
        
        // Save business data
        const response = await helper.customPost(
          `${process.env.VUE_APP_API}/businesses`, 
          businessData
        );

        if (response?.data?.id) {
          this.business = {
            ...this.business,
            ...response.data,
            metadata: response.data.metadata || businessData.metadata
          }
          // Creating the first business also creates the current user's owner
          // membership. Keep the client session in sync before Dashboard mounts.
          this.$store.commit('SET_PERMISSIONS', { role: 'owner', permissions: [] })
          if (this.logoFile) {
            const logoUrl = await this.onUpload(this.logoFile, `businesses/${this.business.id}/logo/${helper.generateRandString(20)}`);
            if (logoUrl) {
              businessData.logo = logoUrl
              const logoResponse = await helper.customPost(
                `${process.env.VUE_APP_API}/businesses`, 
                businessData,
                { showToast: false }
              );
              if (logoResponse?.data?.id) {
                this.business = {
                  ...this.business,
                  ...logoResponse.data,
                  metadata: logoResponse.data.metadata || businessData.metadata
                }
              }
            }
          }
        }

        this.$store.commit('SET_BUSINESS',this.business)

          await helper.customPost(`${process.env.VUE_APP_API}/modules`, this.$store.state.modules, { showToast: false });

         //remove it 
        if(localStorage.getItem('affiliate_ref')){
          localStorage.removeItem('affiliate_ref')
        }
            clearStoredPlanIntent()
            this.$toast.open({ message: this.$t('saved_successfully'), type: 'success' });
            
            this.$router.push({
              path: '/',
              query: {
                welcome: 'true'
              }
            })
       
      } catch (error) {
        this.$toast.error(this.$t('error_saving_business_data'));
      } finally {
        this.loading = false;
      }
    },
    
    async previewImage(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.loadingImage = true;
      
      try {
        // Show preview
        const reader = new FileReader();
        reader.onload = (e) => {
          this.business.logo = e.target.result;
        };
        reader.readAsDataURL(file);
        
        this.logoFile = file
      } catch (error) {
        this.$toast.error(this.$t('error_uploading_image'));
      } finally {
        this.loadingImage = false;
      }
    },
    
    async onUpload(file, path = null) {
      try {
        // if(this.business && this.business.status != 'active') return;
        const url = await helper.customUploadFile(
          file, 
          path || `businesses/${this.business.id}/logo/${helper.generateRandString(20)}`,
          {public: true}
        );
        this.business.logo = url;
        return url; // Return the URL for chaining if needed
      } catch (error) {
        this.$toast.error(this.$t('error_uploading_image'));
      }
    },
    
    goTo(step) {
      if (step === 'general_info') {
        this.stepIndex = 1;
      } else if (step === 'modules' || step === 'plans') {
        if (!this.isGeneralInfoValid) {
          this.$toast.error(this.$t('please_fill_all_required_fields'));
          return;
        }
        this.stepIndex = 2;
      } else if (step === 'summary') {
        if (!this.isGeneralInfoValid) {
          this.$toast.error(this.$t('please_fill_all_required_fields'));
          this.stepIndex = 1;
          return;
        }
        if (!this.isModuleSelected) {
          this.$toast.error('Veuillez choisir un plan');
          this.stepIndex = 2;
          return;
        }
        this.stepIndex = 3;
      }
    },
    
    isStepCompleted(step) {
      if (step === 1) {
        return this.isGeneralInfoValid;
      } else if (step === 2) {
        return this.isModuleSelected;
      } else if (step === 3) {
        return this.canCreateAccount;
      }
      return false;
    },

    selectProjectType(typeKey) {
      this.selectedProjectType = typeKey;

      if (!this.modules || !this.modules.length) return;

      // helper to reset all modules/items
      const resetAll = () => {
        this.modules.forEach(m => {
          if (m.module_key !== 'settings') {
            m.enabled = false;
            m.expanded = false;
            if (m.items && m.items.length) m.items.forEach(i => i.enabled = false);
          } else {
            // keep settings enabled
            m.expanded = false;
          }
        });
      };

      // Utility to find and enable module/item by module_key and item titles list
      const enableModuleItems = (moduleKey, itemTitles = []) => {
        const mod = this.modules.find(m => m.module_key === moduleKey);
        if (!mod) return;
        mod.enabled = true;
        mod.expanded = true;
        if (mod.items && mod.items.length) {
          // default disable all items first
          mod.items.forEach(it => { it.enabled = false });
          if (!itemTitles || !itemTitles.length) {
            // enable all items
            mod.items.forEach(it => it.enabled = true);
          } else {
            mod.items.forEach(it => {
              if (itemTitles.includes(it.title)) it.enabled = true;
            });
          }
        }
      };

      // Reset everything except settings then enable per template
      resetAll();

      if (typeKey === 'pos') {
        enableModuleItems('dashboard');
        enableModuleItems('crm', ['clients']);
        enableModuleItems('inventory', ['products']);
        enableModuleItems('pos', ['pos', 'pos_transactions', 'pos_cash_registers', 'pos_insights']);
      } else if (typeKey === 'cash') {
        // Cash Management: enable only finance module (payments, etc.)
        // Do not auto-enable dashboard or crm for cash-only setup
        enableModuleItems('finance', ['payments']);
      } else if (typeKey === 'files') {
        // File Management: enable only attachments module
        // Do not auto-enable dashboard or crm for file-only setup
        enableModuleItems('attachments');
      } else if (typeKey === 'inventory') {
        // Inventory / Invoicing: dashboard, crm.clients, inventory.products, sales:quotes/orders/invoices/deliveries/insights
        enableModuleItems('dashboard');
        enableModuleItems('crm', ['clients']);
        enableModuleItems('inventory', ['products']);
        enableModuleItems('sales', ['quotes', 'orders', 'invoices', 'deliveries']);
      } else if (typeKey === 'project') {
        // Project management: dashboard, crm.clients, project_management: projects, tasks
        enableModuleItems('dashboard');
        enableModuleItems('crm', ['clients']);
        enableModuleItems('project_management', ['projects', 'tasks']);
      } else if (typeKey === 'reservations') {
        // Reservations: dashboard, crm.clients, reservations
        enableModuleItems('dashboard');
        enableModuleItems('crm', ['clients']);
        enableModuleItems('reservations', ['reservations']);
      } else if (typeKey === 'subscriptions') {
        // Subscriptions/Gym: dashboard, crm.clients, subscription_management (if present)
        enableModuleItems('dashboard');
        enableModuleItems('crm', ['clients']);
        enableModuleItems('subscription_management', ['overview', 'subscriptions', 'plans', 'attendance']);
      } else if (typeKey === 'hr') {
        enableModuleItems('dashboard');
        enableModuleItems('hr');
      } else if (typeKey === 'contracts') {
        enableModuleItems('dashboard');
        enableModuleItems('crm', ['clients']);
        enableModuleItems('contracts');
      } else if (typeKey === 'planning') {
        enableModuleItems('dashboard');
        enableModuleItems('crm', ['clients']);
        enableModuleItems('planning');
      } else if (typeKey === 'leads') {
        enableModuleItems('dashboard');
        enableModuleItems('crm', ['clients']);
        enableModuleItems('leads');
      } else if (typeKey === 'custom') {
        // Custom: allow user to toggle modules manually, do not auto-change other than ensuring modules present
        // keep current modules as-is (after resetAll they are disabled), so enable only settings by default
        // leave it to user to choose
      }

      this.applySelectedPlanToModules();

      // commit changes to the store so other components observe the new module configuration
      try {
        const cloned = JSON.parse(JSON.stringify(this.modules));
        this.$store.commit('SET_MODULES', cloned);
      } catch (e) {
        alert('Failed to commit modules to store:');
      }
    },
    
    loadCountries() {
      // You can load countries from an API or use a static list
      this.countries = [
        { code: 'MA', value: 'morocco', name: 'Morocco' },
        { code: 'FR', value: 'france', name: 'France' },
        { code: 'US', value: 'united_states', name: 'United States of America' },
        { code: 'DE', value: 'germany', name: 'Germany' },
        { code: 'EG', value: 'egypt', name: 'Egypt' },
        { code: 'ES', value: 'spain', name: 'Spain' },
        { code: 'GB', value: 'united_kingdom', name: 'United Kingdom' },
        { code: 'IT', value: 'italy', name: 'Italy' },
        { code: 'NL', value: 'netherlands', name: 'Netherlands' },
        { code: 'DZ', value: 'algeria', name: 'Algeria' },
        { code: 'TN', value: 'tunisia', name: 'Tunisia' },
        { code: 'AO', value: 'angola', name: 'Angola' },
        { code: 'BJ', value: 'benin', name: 'Benin' },
        { code: 'BW', value: 'botswana', name: 'Botswana' },
        { code: 'CD', value: 'democratic_republic_of_congo', name: 'Democratic Republic of the Congo' },
        { code: 'CF', value: 'central_african_republic', name: 'Central African Republic' },
        { code: 'CG', value: 'republic_of_congo', name: 'Republic of the Congo' },
        { code: 'CI', value: 'cote_d_ivoire', name: "Côte d'Ivoire" },
        { code: 'DJ', value: 'djibouti', name: 'Djibouti' },
        { code: 'ER', value: 'eritrea', name: 'Eritrea' },
        { code: 'ET', value: 'ethiopia', name: 'Ethiopia' },
        { code: 'GA', value: 'gabon', name: 'Gabon' },
        { code: 'GH', value: 'ghana', name: 'Ghana' },
        { code: 'GM', value: 'gambia', name: 'The Gambia' },
        { code: 'GN', value: 'guinea', name: 'Guinea' },
        { code: 'GQ', value: 'equatorial_guinea', name: 'Equatorial Guinea' },
        { code: 'KE', value: 'kenya', name: 'Kenya' },
        { code: 'KM', value: 'comoros', name: 'Comoros' },
        { code: 'LR', value: 'liberia', name: 'Liberia' },
        { code: 'LS', value: 'lesotho', name: 'Lesotho' },
        { code: 'LY', value: 'libya', name: 'Libya' },
        { code: 'MG', value: 'madagascar', name: 'Madagascar' },
        { code: 'ML', value: 'mali', name: 'Mali' },
        { code: 'MR', value: 'mauritania', name: 'Mauritania' },
        { code: 'MW', value: 'malawi', name: 'Malawi' },
        { code: 'MU', value: 'mauritius', name: 'Mauritius' },
        { code: 'MZ', value: 'mozambique', name: 'Mozambique' },
        { code: 'NA', value: 'namibia', name: 'Namibia' },
        { code: 'NE', value: 'niger', name: 'Niger' },
        { code: 'NG', value: 'nigeria', name: 'Nigeria' },
        { code: 'RE', value: 'réunion', name: 'Réunion' },
        { code: 'RW', value: 'rwanda', name: 'Rwanda' },
        { code: 'SC', value: 'seychelles', name: 'Seychelles' },
        { code: 'SD', value: 'sudan', name: 'Sudan' },
        { code: 'SH', value: 'saint_helena', name: 'Saint Helena, Ascension and Tristan da Cunha' },
        { code: 'SL', value: 'sierra_leone', name: 'Sierra Leone' },
        { code: 'SN', value: 'senegal', name: 'Senegal' },
        { code: 'SO', value: 'somalia', name: 'Somalia' },
        { code: 'SS', value: 'south_sudan', name: 'South Sudan' },
        { code: 'ST', value: 'são_tomé_and_príncipe', name: 'São Tomé and Principe' },
        { code: 'SZ', value: 'swaziland', name: 'Swaziland' },
        { code: 'TD', value: 'chad', name: 'Chad' },
        { code: 'TG', value: 'togo', name: 'Togo' },
        { code: 'TZ', value: 'tanzania', name: 'Tanzania' },
        { code: 'UG', value: 'uganda', name: 'Uganda' },
        { code: 'ZA', value: 'south_africa', name: 'South Africa' },
        { code: 'ZM', value: 'zambia', name: 'Zambia' },
        { code: 'ZW', value: 'zimbabwe', name: 'Zimbabwe' }
      ];
    }
  }
}
</script>

<style scoped>
@media (max-width: 576px) {
  .onboarding-shell {
    padding-left: 12px;
    padding-right: 12px;
  }

  .onboarding-header {
    display: block !important;
  }
  .onboarding-header h5 {
    margin-bottom: 1rem;
  }
  .onboarding-next-btn {
    display: block;
    width: 100%;
    margin-top: 0.5rem;
  }
  .btn-success,
  .btn-outline-secondary {
    margin-top: 0.5rem !important;
  }
}
.onboarding-container {
  min-height: 100vh;
  background-color: #f8f9fa;
  padding: 2rem 0;
}

.onboarding-shell {
  max-width: 1360px;
  padding-left: 24px;
  padding-right: 24px;
}

/* Custom Stepper Styles */
.progress-stepper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
}

.step {
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  z-index: 2;
  background: white;
  padding: 0.5rem;
  transition: all 0.3s ease;
}

.step:hover .step-icon {
  transform: scale(1.1);
}

.summary-layout {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.summary-panel {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 22px;
}

.summary-panel-header {
  align-items: flex-start;
  border-bottom: 1px solid #eef2f7;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 18px;
  padding-bottom: 14px;
}

.summary-panel-header span {
  color: #64748b;
  display: block;
  font-size: 0.74rem;
  font-weight: 900;
  letter-spacing: 0;
  margin-bottom: 3px;
  text-transform: uppercase;
}

.summary-panel-header h6 {
  color: #0f172a;
  font-size: 1.08rem;
  font-weight: 900;
  margin: 0;
}

.summary-list {
  display: grid;
  gap: 14px 18px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin: 0;
}

.summary-list div {
  min-width: 0;
}

.summary-list div.full {
  grid-column: 1 / -1;
}

.summary-list dt {
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 850;
  margin-bottom: 4px;
}

.summary-list dd {
  color: #0f172a;
  font-size: 0.95rem;
  font-weight: 800;
  margin: 0;
  overflow-wrap: anywhere;
}

.plan-summary-panel {
  background: #f8fafc;
}

.summary-plan-price {
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}

.summary-plan-price strong {
  color: #0f172a;
  font-size: 2rem;
  font-weight: 950;
  line-height: 1;
}

.summary-plan-price span {
  color: #64748b;
  font-size: 0.86rem;
  font-weight: 850;
}

.summary-plan-price .summary-original-price {
  color: #94a3b8;
  text-decoration: line-through;
  width: 100%;
}

.summary-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.summary-badges span {
  align-items: center;
  background: #eef6ff;
  border-radius: 999px;
  color: #1d4ed8;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 850;
  gap: 6px;
  padding: 7px 10px;
}

.summary-modules-title {
  color: #0f172a;
  font-size: 1.2rem;
  font-weight: 900;
  margin: 3px 0 0;
}

.summary-modules {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
}

.summary-module-card {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: center;
  min-height: 112px;
  padding: 14px 10px;
  text-align: center;
}

.summary-module-icon {
  align-items: center;
  border-radius: 14px;
  display: inline-flex;
  flex: 0 0 56px;
  height: 56px;
  justify-content: center;
  width: 56px;
}

.summary-module-icon i {
  font-size: 1.75rem;
}

.summary-module-name {
  color: #334155;
  font-size: 0.82rem;
  font-weight: 850;
  line-height: 1.25;
}

.summary-feature-list {
  display: grid;
  gap: 9px;
  list-style: none;
  margin: 18px 0 0;
  padding: 0;
}

.summary-feature-list li {
  align-items: flex-start;
  color: #334155;
  display: flex;
  font-size: 0.9rem;
  font-weight: 750;
  gap: 8px;
}

.summary-feature-list i {
  color: #16a34a;
  font-size: 1rem;
}

.summary-actions {
  gap: 12px;
}

@media (max-width: 900px) {
  .summary-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 576px) {
  .summary-list {
    grid-template-columns: 1fr;
  }

  .summary-actions {
    flex-direction: column;
  }
}

.step-icon-wrapper {
  position: relative;
}

.step-icon {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background-color: #f8f9fa;
  border: 2px solid #dee2e6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: #6c757d;
  font-size: 1.1rem;
  transition: all 0.3s ease;
}

.step.active .step-icon {
  background-color: #fff;
  border-color: #303030;
  color: #303030;
  box-shadow: 0 0 0 4px rgba(48, 48, 48, 0.1);
}

.step.completed .step-icon {
  background-color: #198754;
  border-color: #198754;
  color: white;
}

.step-content {
  display: flex;
  flex-direction: column;
}

.step-title {
  margin: 0;
  font-weight: 600;
  color: #495057;
  transition: color 0.3s ease;
}

.step.active .step-title {
  color: #303030;
}

.step.completed .step-title {
  color: #198754;
}

.step-divider {
  flex-grow: 1;
  height: 2px;
  background-color: #dee2e6;
  margin: 0 1rem;
  position: relative;
  transition: all 0.3s ease;
}

.step-divider.active {
  background-color: #198754;
}

@media (max-width: 768px) {
  .progress-stepper {
    gap: 0.5rem;
  }
  
  .step {
    padding: 0;
    gap: 0.5rem;
  }
  
  .step-icon {
    width: 35px;
    height: 35px;
    font-size: 0.9rem;
  }
  
  .step-title {
    font-size: 0.9rem;
  }
  
  .step-desc {
    display: none;
  }
  
  .step-divider {
    margin: 0 0.5rem;
  }

  .pricing-onboarding-head,
  .selected-plan-main {
    display: block;
  }

  .selected-plan-price {
    margin-top: 14px;
    width: 100%;
  }

  .included-module-grid {
    grid-template-columns: 1fr;
  }

  .onboarding-market-notes span,
  .onboarding-addons span {
    justify-content: center;
    width: 100%;
  }
}

.logo-upload-container {
  width: 100px;
  height: 100px;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s ease;
}

.logo-upload-container:hover {
  border-color: #303030;
  background-color: #f8f9ff;
}

.logo-upload-container .preview {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.module-card {
  transition: all 0.3s ease;
  border: none !important;
}

.module-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
}

.active-app-shadow {
    box-shadow: 0 0 0 2px #4A4A4A20, 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
}

.deactivated-module {
    opacity: 0.6;
    filter: grayscale(0.4);
}

.module-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.module-card:hover .module-icon {
  transform: scale(1.1);
}

.project-type-card {
  transition: all 0.3s ease;
  border: 2px solid #dee2e6;
}
.project-type-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1);
  border-color: #303030;
}
.project-type-card.project-type-selected {
  border-color: #303030;
  background-color: #f8f9ff;
  box-shadow: 0 0.5rem 1rem rgba(48, 48, 48, 0.15);
}
.project-type-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  transition: all 0.3s ease;
  flex-shrink: 0;
}
.project-type-card:hover .project-type-icon {
  transform: scale(1.1);
}

.pricing-onboarding {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 18px;
}

.pricing-onboarding-head {
  align-items: flex-end;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  margin-bottom: 14px;
}

.pricing-onboarding-head h6 {
  color: #111827;
  font-weight: 800;
  margin: 0;
}

.pricing-onboarding-head p {
  color: #64748b;
  font-size: 0.85rem;
  margin: 0;
  max-width: 430px;
}

.pricing-kicker {
  color: #2563eb;
  display: block;
  font-size: 0.68rem;
  font-weight: 800;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.eyebrow {
  color: #2563eb;
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0;
  margin-bottom: 12px;
  text-transform: uppercase;
}

.onboarding-market-notes,
.onboarding-addons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.onboarding-market-notes span,
.onboarding-addons span {
  align-items: center;
  background: #fff;
  border: 1px solid #dbe4f0;
  border-radius: 999px;
  color: #334155;
  display: inline-flex;
  font-size: 0.76rem;
  font-weight: 800;
  gap: 6px;
  min-height: 30px;
  padding: 0 10px;
}

.onboarding-market-notes i {
  color: #16a34a;
}

.onboarding-plan-card {
  background: #fff;
  border: 1px solid #dbe4f0;
  border-radius: 8px;
  color: #111827;
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-height: 116px;
  padding: 14px;
  position: relative;
  text-align: left;
  width: 100%;
}

.onboarding-plan-card:hover {
  border-color: #93c5fd;
}

.onboarding-plan-card.active {
  border-color: #2563eb;
  box-shadow: 0 10px 24px rgba(37, 99, 235, 0.16);
}

.onboarding-plan-card strong {
  font-size: 1rem;
  font-weight: 800;
}

.plan-price-mini {
  color: #0f172a;
  font-size: 0.88rem;
  font-weight: 800;
}

.onboarding-plan-card small {
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 700;
}

.plan-feature-list .bi-stars {
  color: #93c5fd;
}

.plan-mini-badge {
  background: #2563eb;
  border-radius: 6px;
  color: #fff;
  font-size: 0.62rem;
  font-weight: 800;
  padding: 3px 7px;
  position: absolute;
  right: 10px;
  top: -9px;
  text-transform: uppercase;
}

.selected-plan-panel {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.07);
  padding: 22px;
}

.selected-plan-main {
  align-items: flex-start;
  display: flex;
  gap: 24px;
  justify-content: space-between;
}

.selected-plan-title h5 {
  color: #0f172a;
  font-weight: 850;
  margin: 0 0 5px;
}

.selected-plan-title p {
  color: #64748b;
  font-size: 0.9rem;
  margin: 0;
  max-width: 520px;
}

.selected-plan-price {
  align-items: baseline;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #0f172a;
  display: flex;
  gap: 5px;
  justify-content: center;
  min-width: 150px;
  padding: 12px 14px;
  text-align: center;
  white-space: nowrap;
}

.selected-plan-price strong,
.selected-plan-price span {
  font-size: 1.05rem;
  font-weight: 850;
}

.selected-plan-price small {
  color: #64748b;
  font-weight: 700;
}

.plan-limits {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 18px 0 12px;
}

.plan-limits span {
  align-items: center;
  background: #f1f5f9;
  border-radius: 999px;
  color: #334155;
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 750;
  gap: 7px;
  padding: 7px 11px;
}

.onboarding-addons span {
  border-radius: 8px;
}

.plan-section-title {
  color: #111827;
  font-size: 0.82rem;
  font-weight: 850;
  margin-bottom: 10px;
  text-transform: uppercase;
}

.plan-feature-list {
  display: grid;
  gap: 9px;
}

.plan-feature-list span {
  align-items: center;
  color: #334155;
  display: flex;
  font-size: 0.9rem;
  font-weight: 650;
  gap: 8px;
}

.plan-feature-list i {
  color: #16a34a;
  font-size: 1rem;
}

.included-module-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.included-module-chip {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #1f2937;
  display: flex;
  font-size: 0.86rem;
  font-weight: 750;
  gap: 9px;
  min-height: 44px;
  padding: 9px 10px;
}

.plans-card-grid {
  align-items: start;
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-top: 22px;
}

.onboarding-package-card {
  background: #fff;
  border: 1px solid #dbe4f0;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.05);
  box-sizing: border-box;
  color: #0f172a;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  padding: 24px 20px 20px;
  position: relative;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.onboarding-package-card:hover {
  border-color: #93c5fd;
  box-shadow: 0 16px 38px rgba(37, 99, 235, 0.1);
}

.onboarding-package-card.selected {
  border-color: #1765cc;
  box-shadow: inset 0 0 0 2px #1765cc, 0 18px 42px rgba(37, 99, 235, 0.14);
}

.plan-card-recommended-badge {
  background: #0758bb;
  border-radius: 0 12px 0 10px;
  color: #fff;
  font-size: 0.68rem;
  font-weight: 900;
  padding: 11px 13px;
  position: absolute;
  right: 0;
  text-transform: uppercase;
  top: 0;
}

.package-top {
  min-height: 132px;
  padding-top: 8px;
}

.plan-card-kicker {
  color: #718096;
  display: block;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  margin-bottom: 14px;
  padding-right: 82px;
  text-transform: uppercase;
}

.package-top h3 {
  color: #0b1f3a;
  font-size: 1.65rem;
  font-weight: 950;
  letter-spacing: -0.035em;
  margin: 0 0 10px;
}

.package-top p {
  color: #64748b;
  font-size: 0.88rem;
  line-height: 1.55;
  margin: 0;
}

.package-price {
  align-items: flex-start;
  border-bottom: 1px solid #e7edf4;
  border-top: 1px solid #e7edf4;
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: center;
  min-height: 124px;
  padding: 16px 0;
}

.package-price.enterprise {
  justify-content: center;
}

.package-price-number {
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  line-height: 1;
}

.package-price strong {
  color: #0b1f3a;
  font-size: 2.3rem;
  font-weight: 950;
  letter-spacing: -0.045em;
}

.package-price-number .package-price-period {
  color: #0b1f3a;
  font-size: 0.88rem;
  font-weight: 950;
  letter-spacing: 0.01em;
}

.package-price .package-original-price {
  color: #94a3b8;
  font-size: 0.75rem;
  font-weight: 800;
  text-decoration: line-through;
}

.package-price small {
  color: #7c8ba1;
  font-size: 0.73rem;
  font-weight: 700;
}

.package-price .package-saving {
  color: #07854e;
  font-weight: 850;
}

.package-price-quote {
  font-size: 2rem !important;
}

.plan-stat-list {
  border-bottom: 1px solid #e7edf4;
  display: grid;
  gap: 11px;
  list-style: none;
  margin: 0;
  padding: 16px 0;
}

.plan-stat-list li {
  align-items: flex-start;
  color: #475569;
  display: flex;
  font-size: 0.8rem;
  font-weight: 750;
  gap: 9px;
  line-height: 1.35;
}

.plan-stat-list i {
  color: #1765cc;
  flex: 0 0 18px;
  font-size: 0.95rem;
  margin-top: 1px;
  text-align: center;
}

.plan-details-toggle {
  align-items: center;
  background: #f4f8ff;
  border: 1px solid #d7e6fb;
  border-radius: 9px;
  color: #0758bb;
  display: flex;
  font-size: 0.76rem;
  font-weight: 900;
  gap: 10px;
  justify-content: space-between;
  margin: 14px 0 12px;
  min-height: 42px;
  padding: 8px 11px;
  text-align: left;
  width: 100%;
}

.plan-details-toggle:hover,
.plan-details-toggle[aria-expanded="true"] {
  background: #eaf2ff;
  border-color: #a8c8f2;
}

.plan-details-toggle i {
  flex: 0 0 auto;
  font-size: 0.9rem;
}

.plan-expanded-details {
  border-top: 1px solid #e7edf4;
}

.plan-modules-section,
.plan-features-section {
  border-bottom: 1px solid #e7edf4;
  padding: 22px 0;
}

.plan-features-section {
  margin-bottom: 18px;
}

.package-section-heading {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 14px;
}

.package-section-heading h4,
.package-section-title {
  color: #12233f;
  font-size: 0.72rem;
  font-weight: 950;
  letter-spacing: 0.04em;
  margin: 0;
  text-transform: uppercase;
}

.package-section-heading > span {
  align-items: center;
  background: #eaf2ff;
  border-radius: 999px;
  color: #1765cc;
  display: inline-flex;
  font-size: 0.72rem;
  font-weight: 950;
  height: 30px;
  justify-content: center;
  min-width: 30px;
  padding: 0 8px;
}

.plan-module-grid {
  display: grid;
  gap: 10px 8px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.plan-module-item {
  align-items: center;
  color: #334155;
  display: flex;
  font-size: 0.7rem;
  font-weight: 850;
  gap: 7px;
  min-width: 0;
}

.plan-module-item > span:last-child {
  line-height: 1.25;
  min-width: 0;
  overflow-wrap: anywhere;
}

.plan-module-icon {
  align-items: center;
  border-radius: 8px;
  display: inline-flex;
  flex: 0 0 34px;
  height: 34px;
  justify-content: center;
  width: 34px;
}

.plan-module-icon i {
  font-size: 1rem;
}

.package-section-title {
  margin-bottom: 14px;
}

.package-feature-list {
  display: grid;
  gap: 10px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.package-feature-list li {
  align-items: flex-start;
  color: #475569;
  display: flex;
  font-size: 0.76rem;
  font-weight: 700;
  gap: 8px;
  line-height: 1.4;
}

.package-feature-list i {
  color: #10a36a;
  flex: 0 0 auto;
  font-size: 0.9rem;
  margin-top: 1px;
}

.package-selection-state {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #d7e0ea;
  border-radius: 9px;
  color: #334155;
  display: flex;
  font-size: 0.82rem;
  font-weight: 900;
  gap: 8px;
  justify-content: center;
  margin-top: auto;
  min-height: 46px;
  padding: 0 12px;
  width: 100%;
}

.package-selection-state:hover,
.package-selection-state.primary,
.package-selection-state.selected {
  background: #0758bb;
  border-color: #0758bb;
  color: #fff;
}

@media (max-width: 1200px) {
  .plans-card-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767.98px) {
  .plans-card-grid {
    grid-template-columns: 1fr;
  }

  .onboarding-package-card {
    padding: 22px 18px 18px;
  }

  .package-top,
  .package-price,
  .plan-stat-list {
    min-height: 0;
  }
}

.onboarding-trial-pill {
  align-items: center;
  background: linear-gradient(120deg, #ecfdf5 0%, #eff6ff 100%);
  border: 1px solid #6ee7b7;
  border-radius: 999px;
  color: #166534;
  display: inline-flex;
  gap: 9px;
  margin: -4px 0 10px auto;
  min-height: 42px;
  overflow: hidden;
  padding: 4px 11px 4px 5px;
  position: relative;
  box-shadow: 0 6px 18px rgba(16, 185, 129, 0.13);
  width: fit-content;
}

.onboarding-trial-pill-icon {
  align-items: center;
  background: linear-gradient(135deg, #16a34a, #059669);
  border-radius: 50%;
  box-shadow: 0 3px 8px rgba(5, 150, 105, 0.25);
  color: #fff;
  display: inline-flex;
  flex: 0 0 32px;
  height: 32px;
  justify-content: center;
  width: 32px;
}

.onboarding-trial-pill-icon i {
  font-size: 0.95rem;
}

.onboarding-trial-pill-copy {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
}

.onboarding-trial-pill strong {
  color: #14532d;
  font-size: 0.8rem;
  font-weight: 950;
}

.onboarding-trial-pill small {
  color: #527064;
  font-size: 0.63rem;
  font-weight: 750;
  margin-top: 2px;
}

.onboarding-trial-pill-sparkle {
  color: #f59e0b;
  font-size: 0.9rem;
}

.billing-switch {
  background: #fff;
  border: 1px solid #dbe5f1;
  border-radius: 8px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  max-width: 300px;
  padding: 3px;
}

.billing-switch button {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #475569;
  display: flex;
  font-size: 0.82rem;
  font-weight: 800;
  gap: 5px;
  justify-content: center;
  min-height: 32px;
  padding: 0 8px;
}

.billing-switch button.active {
  background: #004aad;
  color: #fff;
}

.billing-switch span {
  background: rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  font-size: 0.58rem;
  padding: 2px 5px;
}

.comparison-panel {
  background: #fff;
  border: 1px solid #dfe7f0;
  border-radius: 8px;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.08);
  margin-top: 22px;
  padding: 0;
}

.mobile-pricing-list {
  display: none;
}

.mobile-pricing-card {
  background: #fff;
  border: 1px solid #dbe5f1;
  border-radius: 10px;
  color: #0f172a;
  cursor: pointer;
  padding: 14px;
}

.mobile-pricing-card.recommended {
  border-color: #2563eb;
}

.mobile-pricing-card.selected {
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.18);
}

.mobile-pricing-card:focus-visible {
  outline: 2px solid rgba(37, 99, 235, 0.36);
  outline-offset: 3px;
}

.mobile-plan-top {
  align-items: flex-start;
  display: flex;
  gap: 10px;
}

.mobile-plan-icon {
  align-items: center;
  background: #eff6ff;
  border-radius: 8px;
  color: #2563eb;
  display: inline-flex;
  flex: 0 0 40px;
  height: 40px;
  justify-content: center;
  width: 40px;
}

.mobile-plan-title-row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.mobile-plan-title-row h6 {
  font-size: 1.05rem;
  font-weight: 900;
  margin: 0;
}

.mobile-plan-title-row span {
  background: #eff6ff;
  border-radius: 999px;
  color: #1d4ed8;
  font-size: 0.62rem;
  font-weight: 900;
  padding: 3px 7px;
  text-transform: uppercase;
}

.mobile-plan-title-row span.selected {
  background: #dcfce7;
  color: #166534;
}

.mobile-plan-top p {
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 750;
  margin: 3px 0 0;
}

.mobile-plan-price {
  align-items: baseline;
  border-bottom: 1px solid #e2e8f0;
  border-top: 1px solid #e2e8f0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 12px 0;
  padding: 12px 0;
}

.mobile-plan-price.enterprise {
  align-items: center;
}

.mobile-plan-price strong {
  font-size: 1.85rem;
  font-weight: 900;
  line-height: 1;
}

.mobile-plan-price span {
  color: #64748b;
  font-size: 0.82rem;
  font-weight: 850;
}

.mobile-plan-price .plan-price-original {
  color: #94a3b8;
  text-decoration: line-through;
  width: 100%;
}

.mobile-plan-price .plan-discount-badge {
  background: #dcfce7;
  border-radius: 999px;
  color: #166534;
  padding: 3px 8px;
}

.mobile-plan-limits {
  display: grid;
  gap: 7px;
  margin-bottom: 12px;
}

.mobile-plan-limits span {
  align-items: flex-start;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #334155;
  display: flex;
  font-size: 0.8rem;
  font-weight: 800;
  gap: 8px;
  line-height: 1.25;
  padding: 8px 10px;
}

.mobile-plan-limits i {
  color: #2563eb;
  flex: 0 0 auto;
}

.mobile-plan-features {
  display: grid;
  gap: 7px;
  list-style: none;
  margin: 0 0 14px;
  padding: 0;
}

.mobile-plan-features li {
  align-items: flex-start;
  color: #334155;
  display: flex;
  font-size: 0.82rem;
  font-weight: 700;
  gap: 7px;
  line-height: 1.3;
}

.mobile-plan-features i {
  color: #16a34a;
  flex: 0 0 auto;
  margin-top: 1px;
}

.mobile-plan-action {
  align-items: center;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  color: #0f172a;
  display: flex;
  font-weight: 900;
  justify-content: center;
  min-height: 42px;
  width: 100%;
}

.mobile-plan-action.primary {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}

.comparison-head {
  align-items: flex-end;
  display: flex;
  gap: 24px;
  justify-content: space-between;
  margin-bottom: 18px;
}

.comparison-head h2 {
  color: #0f172a;
  font-size: 1.35rem;
  font-weight: 800;
  margin: 0;
}

.comparison-head p {
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.45;
  margin: 0;
  max-width: 470px;
}

.comparison-table-wrap {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow-x: auto;
}

.comparison-table {
  border-collapse: separate;
  border-spacing: 0;
  min-width: 820px;
  width: 100%;
}

.comparison-table th,
.comparison-table td {
  border-bottom: 1px solid #e2e8f0;
  color: #334155;
  font-size: 0.86rem;
  padding: 13px 14px;
  text-align: center;
  vertical-align: middle;
}

.comparison-table thead th {
  background: #f8fafc;
  color: #0f172a;
  font-weight: 800;
  position: sticky;
  top: 0;
  z-index: 1;
}

.comparison-table thead th:not(:first-child) {
  font-weight: 500;
  position: sticky;
}

.comparison-table th:first-child {
  color: #0f172a;
  font-weight: 800;
  left: 0;
  min-width: 210px;
  position: sticky;
  text-align: left;
  z-index: 2;
}

.comparison-table tbody th:first-child {
  background: #fff;
}

.comparison-table thead th:first-child {
  background: #f8fafc;
  z-index: 3;
}

.comparison-table thead th.selected {
  background: #eff6ff;
  color: #1d4ed8;
  overflow: hidden;
}

.comparison-table tbody td.selected,
.comparison-table tfoot td.selected {
  background: #eff6ff;
}

.comparison-table thead th.hovered,
.comparison-table tbody td.hovered,
.comparison-table tfoot td.hovered {
  background: #f8fbff;
  box-shadow: inset 2px 0 #2563eb, inset -2px 0 #2563eb;
}

.comparison-table thead th.hovered {
  box-shadow: inset 2px 0 #2563eb, inset -2px 0 #2563eb, inset 0 2px #2563eb;
}

.comparison-table tfoot td.hovered {
  box-shadow: inset 2px 0 #2563eb, inset -2px 0 #2563eb, inset 0 -2px #2563eb;
}

.comparison-table thead span {
  background: #1d4ed8;
  border-radius: 999px;
  color: #fff;
  display: block;
  font-size: 0.62rem;
  font-weight: 800;
  margin: 5px auto 0;
  padding: 3px 7px;
  width: fit-content;
}

.comparison-table thead .selected-plan-chip {
  background: #dcfce7;
  border-radius: 4px;
  color: #166534;
  font-size: 0.6rem;
  margin: 0;
  padding: 0;
  position: absolute;
  right: -46px;
  text-align: center;
  top: 18px;
  transform: rotate(45deg);
  transform-origin: center;
  white-space: nowrap;
  width: 150px;
}

.comparison-table tr:last-child th,
.comparison-table tr:last-child td {
  border-bottom: 0;
}

.comparison-table .plan-choice-cell {
  cursor: pointer;
}

.comparison-table tbody td,
.comparison-table tfoot td {
  cursor: pointer;
}

.comparison-table .plan-choice-cell:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 2px rgba(37, 99, 235, 0.28);
}

.comparison-table .plan-choice-cell:hover {
  background: #f8fafc;
}

.choose-plan-btn {
  align-items: center;
  background: transparent;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  color: #0f172a;
  display: inline-flex;
  font-size: 0.84rem;
  font-weight: 800;
  justify-content: center;
  min-height: 38px;
  padding: 0 14px;
  width: 100%;
}

.choose-plan-btn.primary {
  background: transparent;
  border-color: #2563eb;
  color: #2563eb;
}

.choose-plan-btn:hover {
  transform: translateY(-1px);
}

.comparison-value {
  align-items: center;
  display: inline-flex;
  font-weight: 500;
  justify-content: center;
  min-height: 24px;
}

.comparison-value.included {
  background: #dcfce7;
  border-radius: 50%;
  color: #166534;
  height: 24px;
  width: 24px;
}

.comparison-value.unavailable {
  color: #cbd5e1;
}

@media (max-width: 768px) {
  .comparison-panel {
    background: transparent;
    border: 0;
    box-shadow: none;
    padding: 0;
  }

  .mobile-pricing-list {
    display: grid;
    gap: 12px;
  }

  .comparison-table-wrap {
    display: none;
  }

  .comparison-head {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }
}

.included-module-icon {
  align-items: center;
  border-radius: 7px;
  color: #fff;
  display: inline-flex;
  flex: 0 0 28px;
  height: 28px;
  justify-content: center;
  width: 28px;
}

.included-module-icon i {
  font-size: 0.9rem;
}

.plan-unavailable-module {
  cursor: not-allowed !important;
  filter: grayscale(1);
  opacity: 0.55;
}

.plan-locked-label {
  color: #dc3545;
  display: block;
  font-size: 10px;
  font-weight: 700;
  margin-top: 5px;
}



/* User Count Step Styles */
.user-count-card {
  border: 2px solid #e9ecef;
  border-radius: 16px;
  transition: all 0.3s ease;
}

.user-count-card:hover {
  border-color: #303030;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1);
}

.user-count-icon {
  font-size: 4rem;
  color: #303030;
}

.user-count-selector {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
}

.user-count-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 120px;
}

.user-count-number {
  font-size: 4rem;
  font-weight: 700;
  color: #303030;
  line-height: 1;
}

.user-count-label {
  font-size: 1rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.pricing-preview {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  padding: 1.5rem;
}

.price-calculation {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.price-item {
  font-size: 1.25rem;
  font-weight: 600;
  color: #495057;
}

.price-operator {
  font-size: 1.25rem;
  color: #6c757d;
}

.price-total {
  font-size: 1.5rem;
  font-weight: 700;
  color: #198754;
}

/* Checkout Step Styles */
.checkout-card {
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
}

.checkout-card .card-header {
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 1.25rem;
}

.checkout-card .card-header h6 {
  font-weight: 600;
  color: #303030;
}

.order-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px dashed #e9ecef;
}

.order-line:last-of-type {
  border-bottom: none;
}

.order-line.total {
  border-bottom: none;
  padding-top: 1rem;
}

.order-line.total span:first-child {
  font-weight: 600;
  font-size: 1.1rem;
}

.total-price {
  font-size: 1.5rem;
  font-weight: 700;
  color: #198754;
}

.feature-list-checkout {
  list-style: none;
  padding: 0;
  margin: 0;
}

.feature-list-checkout li {
  padding: 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
}

.feature-list-checkout li i {
  font-size: 1.1rem;
}

.payment-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 2px solid #198754;
}

.payment-icon {
  font-size: 3rem;
  color: #198754;
}

.payment-card .btn-success {
  background: linear-gradient(135deg, #198754 0%, #157347 100%);
  border: none;
  padding: 1rem;
  font-size: 1.1rem;
  font-weight: 600;
}

.payment-card .btn-success:hover {
  background: linear-gradient(135deg, #157347 0%, #146c43 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(25, 135, 84, 0.3);
}

@media (max-width: 768px) {
  .pricing-onboarding-head {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }

  .user-count-selector {
    gap: 1rem;
  }
  
  .user-count-number {
    font-size: 3rem;
  }
  
  .price-calculation {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .price-operator {
    display: none;
  }
}
</style>
